import { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";
import "dayjs/locale/ko";

import {
  fetchVehicleReservations,
  updateVehicleReservationStatus,
} from "@/apis/reservation";
import Dropdown from "@/components/Dropdown/Dropdown";
import TableFilterButton from "@/pages/Admin/components/TableFilterButton";
import type {
  VehicleReservation,
  VehicleReservationStatus,
} from "@/types/vehicleReservation";

import VehicleMasterSection from "./VehicleMasterSection";
import VehicleReservationTable from "./VehicleReservationTable";

type VehicleFilterType = "all" | "pending" | "approved" | "deleted";

const filterButtons: { name: string; type: VehicleFilterType }[] = [
  { name: "승인 요청", type: "pending" },
  { name: "처리 완료", type: "approved" },
  { name: "삭제", type: "deleted" },
];

dayjs.locale("ko");

const VehicleReservationAdmin = () => {
  const [reservations, setReservations] = useState<VehicleReservation[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<VehicleFilterType>("all");
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format("YYYY-MM"));
  const [searchName, setSearchName] = useState("");

  // API에서 예약 데이터 로드
  const loadReservations = useCallback(async () => {
    const rsvtDt = selectedMonth.replace("-", "");
    const data = await fetchVehicleReservations(rsvtDt);
    setReservations(data);
  }, [selectedMonth]);

  useEffect(() => {
    loadReservations();
  }, [loadReservations]);

  // Count pending requests for badge
  const pendingCount = reservations.filter(r => r.status === "pending").length;

  // Filter reservations
  const filteredReservations = useMemo(() => {
    return reservations
      .filter(r => {
        // Status filter
        if (selectedFilter === "all") return true;
        if (selectedFilter === "pending") return r.status === "pending";
        if (selectedFilter === "approved")
          return (
            r.status === "approved" ||
            r.status === "rejected" ||
            r.status === "cancelled"
          );
        if (selectedFilter === "deleted") return r.status === "deleted";
        return true;
      })
      .filter(r => {
        if (!searchName.trim()) return true;
        return r.applicantName?.includes(searchName.trim());
      })
      .sort(
        (a, b) =>
          new Date(a.reservationDate).getTime() -
          new Date(b.reservationDate).getTime(),
      );
  }, [reservations, selectedFilter, searchName]);

  const [actionLoading, setActionLoading] = useState(false);

  const handleUpdateStatus = async (
    id: number,
    status: VehicleReservationStatus,
    reason?: string,
  ) => {
    if (actionLoading) return;
    setActionLoading(true);
    try {
      await updateVehicleReservationStatus(id, status, reason);
      await loadReservations();
    } catch {
      console.error("상태 변경 실패");
    } finally {
      setActionLoading(false);
    }
  };

  const handleClickFilterButton = (e: MouseEvent<HTMLButtonElement>) => {
    const filterType = e.currentTarget.dataset.filter as VehicleFilterType;
    setSelectedFilter(filterType);
  };

  // Generate month options (2024-11 ~ next month)
  const monthOptions = useMemo(() => {
    const start = dayjs("2024-11-01");
    const end = dayjs().add(1, "month");
    const months: string[] = [];
    let current = start;
    while (current.isBefore(end, "month") || current.isSame(end, "month")) {
      months.push(current.format("YYYY-MM"));
      current = current.add(1, "month");
    }
    return months.reverse(); // 최신 월이 위에 오도록
  }, []);

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* 차량(마스터) 관리 */}
      <VehicleMasterSection />

      {/* Filters */}
      <div className="flex flex-row justify-between">
        <div className="relative flex gap-2">
          <TableFilterButton
            data-filter="all"
            isActive={selectedFilter === "all"}
            onClick={handleClickFilterButton}
          >
            전체
          </TableFilterButton>
          <div className="mx-1 border-r border-gray-dull"></div>
          {filterButtons.map(({ name, type }) => (
            <TableFilterButton
              key={type}
              data-filter={type}
              className="px-2 text-small"
              isActive={selectedFilter === type}
              isNew={type === "pending" && pendingCount > 0}
              onClick={handleClickFilterButton}
            >
              {name}
            </TableFilterButton>
          ))}
        </div>
      </div>

      {/* Month selector + Name search */}
      <div className="flex flex-1 flex-row flex-wrap items-center gap-2">
        <div className="w-40">
          <Dropdown
            category="month"
            options={monthOptions.map(m => ({
              id: m,
              name: dayjs(m).format("YYYY년 M월"),
            }))}
            disabled={false}
            onChangeOption={(option: any) => setSelectedMonth(option.id)}
            selectedOption={dayjs(selectedMonth).format("YYYY년 M월")}
          />
        </div>
        <div className="w-36">
          <input
            type="text"
            placeholder="이름 검색"
            value={searchName}
            onChange={e => setSearchName(e.target.value)}
            className="h-10 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-primary"
          />
        </div>
      </div>

      {/* Table */}
      <VehicleReservationTable
        reservations={filteredReservations}
        onUpdateStatus={handleUpdateStatus}
      />
    </div>
  );
};

export default VehicleReservationAdmin;
